import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, AlertCircle, CheckCircle } from 'lucide-react';
import { Contract, ContractProperty } from '../../types/contract';
import { getContractByShareToken, updateContract } from '../../services/contractApiService';

const ContractReviewPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  
  const [contract, setContract] = useState<Contract | null>(null);
  const [properties, setProperties] = useState<ContractProperty[]>([]);
  const [checkedProperties, setCheckedProperties] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedName, setAcceptedName] = useState<string>('');
  const [isAccepted, setIsAccepted] = useState(false); // New state for acceptance status
  
  // Load contract data
  useEffect(() => {
    const loadContract = async () => {
      if (!shareToken) return;
      
      try {
        setIsLoading(true);
        
        const contractData = await getContractByShareToken(shareToken);
        setContract(contractData);
        
        // Extract properties from contract data
        const allProperties: ContractProperty[] = [];
        
        if (contractData.data && contractData.data.events) {
          contractData.data.events.forEach((event: any) => {
            if (event.properties) {
              allProperties.push(...event.properties);
            }
          });
        }
        
        setProperties(allProperties);
        
        // Initialize checked properties from contract data if available
        if (contractData.data && contractData.data.checkedProperties) {
          setCheckedProperties(new Set(contractData.data.checkedProperties));
        }

        // Check if contract is already accepted
        if (contractData.status === 'accepted' && contractData.acceptedByName && contractData.acceptedAt) {
          setIsAccepted(true);
          setAcceptedName(contractData.acceptedByName);
        }

      } catch (error) {
        console.error('Failed to load contract:', error);
        setError('Failed to load contract. The share link may be invalid or expired.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadContract();
  }, [shareToken]);
  
  // Handle property check/uncheck
  const handlePropertyCheck = (propertyId: string) => {
    setCheckedProperties(prev => {
      const updated = new Set(prev);
      
      if (updated.has(propertyId)) {
        updated.delete(propertyId);
      } else {
        updated.add(propertyId);
      }
      
      return updated;
    });
  };
  
  // Handle accept contract
  const handleAcceptContract = async () => {
    if (!contract || !contract.id || !acceptedName) return;
    
    try {
      setIsSubmitting(true);
      
      const acceptanceDate = new Date().toISOString(); // Capture current date/time
      
      // Update contract status to accepted
      await updateContract(contract.id, {
        status: 'accepted',
        acceptedByName: acceptedName,
        acceptedAt: acceptanceDate,
        data: {
          ...contract.data,
          checkedProperties: Array.from(checkedProperties)
        }
      });
      
      // Show success message on the same page
      setIsSubmitting(false);
      setIsAccepted(true); // Set accepted status to true
      setContract(prev => prev ? { ...prev, status: 'accepted', acceptedByName: acceptedName, acceptedAt: acceptanceDate } : null);

    } catch (error) {
      console.error('Failed to accept contract:', error);
      setError('Failed to accept contract. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Calculate progress
  const progress = properties.length > 0
    ? Math.round((checkedProperties.size / properties.length) * 100)
    : 0;
  
  // Check if all properties are checked
  const allPropertiesChecked = properties.length > 0 && checkedProperties.size === properties.length;
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-red-500 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error</h2>
              <p className="text-gray-600 dark:text-gray-300">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!contract) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Contract Not Found</h2>
            <p className="text-gray-600 dark:text-gray-300">The contract you're looking for doesn't exist or has been removed.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isAccepted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Contract Accepted!</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-2">
            Thank you, <span className="font-semibold">{contract.acceptedByName}</span>, for accepting the contract.
          </p>
          {contract.acceptedAt && (
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Accepted on: {new Date(contract.acceptedAt).toLocaleString()}
            </p>
          )}
          <p className="text-gray-600 dark:text-gray-300 mt-4">
            A confirmation has been recorded. You may close this page.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{contract.name}</h1>
              {contract.data && contract.data.customerName && (
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  For: {contract.data.customerName}
                  {contract.data.customerEmail && ` (${contract.data.customerEmail})`}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirmation Progress
            </span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {checkedProperties.size} of {properties.length} properties ({progress}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              className="bg-blue-600 h-2.5 rounded-full"
            />
          </div>
        </div>
        
        {/* Properties List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Contract Properties
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Please review and confirm each property by checking the box.
            </p>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {properties.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500 dark:text-gray-400">No properties found in this contract.</p>
              </div>
            ) : (
              properties.map(property => {
                const isChecked = checkedProperties.has(property.id);
                
                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      isChecked ? 'bg-green-50 dark:bg-green-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 pt-0.5">
                        <button
                          type="button"
                          onClick={() => handlePropertyCheck(property.id)}
                          className={`w-5 h-5 rounded ${
                            isChecked
                              ? 'bg-green-500 text-white'
                              : 'border border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {isChecked && <Check className="w-5 h-5" />}
                        </button>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                            {property.name}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                            {property.dataType}
                          </span>
                          {property.isRequired && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Path: {property.path}
                        </p>
                        {property.normalizedPath && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Mapped to: {property.normalizedPath}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
        
        {/* Accept Button */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="mb-4">
            <label htmlFor="acceptedName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your Name
            </label>
            <input
              type="text"
              id="acceptedName"
              value={acceptedName}
              onChange={(e) => setAcceptedName(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter your full name"
            />
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-600 dark:text-gray-300">
                {allPropertiesChecked && acceptedName
                  ? 'All properties have been confirmed and your name is provided. You can now accept the contract.'
                  : `Please confirm all properties and provide your name before accepting the contract. (${properties.length - checkedProperties.size} remaining)`}
              </p>
            </div>
            
            <button
              type="button"
              onClick={handleAcceptContract}
              disabled={isSubmitting || !allPropertiesChecked || !acceptedName}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Accept Contract
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractReviewPage;
